/**
 * Integrations Google Analytics card — opt-in GA4 conversion forwarding.
 *
 * Forwards each WhatsApp lead's conversion to the customer's own Google
 * Analytics property via the Data Manager API (off by default). Needs the GA4
 * Measurement ID + the numeric Property ID — both entered manually (Option A;
 * auto-discovery is deferred, ROADMAP D24). The snippet-detected Measurement ID
 * is used as a cross-check warning (not a prefill); enabling runs a backend
 * validateOnly check so an unreachable property is caught at save time.
 * Website-origin leads only — CTWA has no _ga cookie. See
 * REFERENCE/plans/PLAN_GA4_FORWARDING.md. Reads/writes /api/clients/{id}/ga-config.
 */
import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { apiRequest, ApiError } from '@/lib/api';
import { platformLogo } from '@/lib/attributionDisplay';
import { Banner } from '@/components/Banner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { SkeletonBox, SkeletonGroup } from '@/components/Skeleton';
import { ErrorState } from '@/components/ErrorState';

interface GaConfig {
  enabled: boolean;
  measurement_id: string;
  property_id: string;
  detected_measurement_id: string;
}

export function IntegrationsGoogleAnalyticsCard({
  clientId,
}: {
  clientId: string;
}) {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ['client', clientId, 'ga-config'],
    queryFn: () => apiRequest<GaConfig>(`/clients/${clientId}/ga-config`),
  });

  return (
    <section className="rounded-3xl bg-card p-7 shadow-card sm:p-9">
      <div className="mb-5 flex items-start gap-5">
        <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-muted">
          <img
            src={platformLogo('google-analytics')}
            alt=""
            className="h-7 w-7"
          />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="text-xl font-bold text-dark">Google Analytics</h3>
          <p className="mt-1 text-base leading-relaxed text-muted-foreground">
            Send your WhatsApp leads to your Google Analytics as conversions, so
            they show up in your GA reports. Website visitors only.
          </p>
        </div>
        {query.data?.enabled && (
          <span className="shrink-0 rounded-full bg-green-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-green-700">
            On
          </span>
        )}
      </div>

      {query.isLoading && (
        <SkeletonGroup className="space-y-3">
          <SkeletonBox className="h-4 w-48" />
          <SkeletonBox className="h-28 w-full rounded-2xl" />
        </SkeletonGroup>
      )}

      {query.isError && (
        <ErrorState
          title="Couldn't load your Google Analytics settings"
          onRetry={() => query.refetch()}
        />
      )}

      {query.data && (
        <GaEditor
          clientId={clientId}
          data={query.data}
          onSaved={() =>
            queryClient.invalidateQueries({
              queryKey: ['client', clientId, 'ga-config'],
            })
          }
        />
      )}
    </section>
  );
}

function GaEditor({
  clientId,
  data,
  onSaved,
}: {
  clientId: string;
  data: GaConfig;
  onSaved: () => void;
}) {
  // Both IDs are entered manually (Option A) — and on purpose: they must belong
  // to the SAME GA4 property, so copying both from one GA-Admin trip guarantees
  // they match. (Auto-detecting the Measurement ID separately could point at a
  // different property than the manual Property ID.) Option B auto-detects both.
  const initial = {
    enabled: data.enabled,
    measurement_id: data.measurement_id || '',
    property_id: data.property_id,
  };
  const [draft, setDraft] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setDraft({
      enabled: data.enabled,
      measurement_id: data.measurement_id || '',
      property_id: data.property_id,
    });
  }, [data.enabled, data.measurement_id, data.property_id]);

  const dirty =
    draft.enabled !== initial.enabled ||
    draft.measurement_id !== initial.measurement_id ||
    draft.property_id !== initial.property_id;

  // Can't forward without both IDs.
  const canEnable =
    draft.measurement_id.trim() !== '' && draft.property_id.trim() !== '';

  // Cross-check: the Measurement ID our snippet saw on the customer's actual site
  // vs. what they typed. A mismatch usually means they entered the wrong property
  // (a real silent-mistake catch). Non-blocking — detection can be stale, or a
  // site can run more than one property; this is a nudge, not a gate.
  const detected = (data.detected_measurement_id || '').trim();
  const enteredId = draft.measurement_id.trim();
  const idMismatch =
    detected !== '' &&
    enteredId !== '' &&
    detected.toUpperCase() !== enteredId.toUpperCase();

  async function save() {
    setSaving(true);
    setError(null);
    try {
      await apiRequest(`/clients/${clientId}/ga-config`, {
        method: 'PATCH',
        body: {
          enabled: draft.enabled && canEnable,
          measurement_id: draft.measurement_id.trim(),
          property_id: draft.property_id.trim(),
        },
      });
      onSaved();
    } catch (e) {
      setError(e instanceof ApiError ? e.detail || e.message : String(e));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-5">
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label>Property ID</Label>
          <Input
            value={draft.property_id}
            onChange={(e) =>
              setDraft((d) => ({ ...d, property_id: e.target.value }))
            }
            placeholder="123456789"
            className="font-mono"
          />
          <p className="text-xs text-muted-foreground">
            GA Admin → Property details → "PROPERTY ID" (top right).
          </p>
        </div>
        <div className="space-y-1.5">
          <Label>Measurement ID</Label>
          <Input
            value={draft.measurement_id}
            onChange={(e) =>
              setDraft((d) => ({ ...d, measurement_id: e.target.value }))
            }
            placeholder="G-XXXXXXXXXX"
            className="font-mono"
          />
          <p className="text-xs text-muted-foreground">
            GA Admin → Data streams → your web stream → Measurement ID (top right).
          </p>
        </div>
      </div>

      {idMismatch && (
        <Banner variant="warn">
          The Google Analytics on your website looks like{' '}
          <strong>{detected}</strong>, but you entered{' '}
          <strong>{enteredId}</strong>. Double-check they're the same property.
        </Banner>
      )}

      <label
        className={
          canEnable ? 'flex cursor-pointer items-start gap-3' : 'flex items-start gap-3 opacity-70'
        }
      >
        <Checkbox
          checked={draft.enabled && canEnable}
          disabled={!canEnable}
          onCheckedChange={(c) => setDraft((d) => ({ ...d, enabled: c === true }))}
          className="mt-0.5"
        />
        <span className="flex-1">
          <span className="block text-sm font-semibold text-dark">
            Forward conversions to Google Analytics
          </span>
          <span className="mt-0.5 block text-xs text-muted-foreground">
            {canEnable
              ? 'Website-origin leads only.'
              : 'Add the Measurement ID + Property ID to turn this on.'}
          </span>
        </span>
      </label>

      {error && <Banner variant="error">{error}</Banner>}

      <Button
        onClick={save}
        disabled={!dirty || saving}
        className="w-full rounded-full bg-brand py-3.5 text-base font-bold text-white hover:bg-brand-dark sm:w-auto sm:px-10"
      >
        {saving ? 'saving...' : 'Save'}
      </Button>
    </div>
  );
}
